import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, useParams } from "react-router-dom";

import PlatformRoutes from "./PlatformRoutes";
import { PROJECTS_ROUTE } from "./routes";
import { setProject, setProjectUuid } from "../../actions/architecture";
import { SmartlistType } from "../../api/MediaAPI.types";
import { TypeEnum } from "../../api/ProjectAPI.types";
import useAbility from "../../hooks/useAbility";
import { NotificationsIndex } from "../../pages/notifications";
import AdvertisingsCreate from "../advertisings/AdvertisingsCreate";
import AdvertisingsList from "../advertisings/AdvertisingsList";
import AdvertisingsPage from "../advertisings/AdvertisingsPage";
import ContentAnalyticsAdmin from "../analytics/contentAnalytics/ContentAnalyticsAdmin";
import ContentItemAnalytics from "../analytics/contentItemAnalytics/ContentItemAnalytics";
import DashboardAdmin from "../analytics/dashboardAnalytics/DashboardAdmin";
import OfferAnalytics from "../analytics/offerAnalytics/OfferAnalytics";
import TransactionsAnalyticsAdmin from "../analytics/transactionAnalytics/TransactionsAnalyticsAdmin";
import UsersAdmin from "../analytics/userAnalytics/UsersAdmin";
import UsersAdminCreate from "../analytics/userAnalytics/UsersAdminCreate";
import UsersAdminUpdate from "../analytics/userAnalytics/UsersAdminUpdate";
import AppearanceIndex from "../appearance/AppearanceIndex";
import Comment from "../comments/Comment";
import Comments from "../comments/Comments";
import DisplaysList from "../displays/DisplaysList";
import DisplaysPage from "../displays/DisplaysPage";
import EDUAdminCreate from "../edu/EDUAdminCreate";
import EDUAdminInstitutions from "../edu/EDUAdminInstitutions";
import EDUAdminUpdate from "../edu/EDUAdminUpdate";
import FreecopyList from "../freecopy/FreecopyList";
import { FreecopyPage } from "../freecopy/FreecopyPage";
import { FreecopyUniqueCodePage } from "../freecopy/FreecopyUniqueCodePage";
import GiftAdminCreate from "../gift/GiftAdminCreate";
import GiftAdminUpdate from "../gift/GiftAdminUpdate";
import GiftList from "../gift/GiftList";
import OfferList from "../offers/OfferList";
import OffersEdit from "../offers/OffersEdit";
import OfferView from "../offers/OfferView";
import ConfigurationAdmin from "../platform/configuration/ConfigurationAdmin";
import ProjectAdminCreate from "../project/ProjectAdminCreate";
import ProjectAdminUpdate from "../project/ProjectAdminUpdate";
import ProjectList from "../project/ProjectList";
import SeriePage from "../series/SeriePage";
import SmartlistSeriesList from "../series/SmartlistSeriesList";
import SmartlistList from "../smartlist/SmartlistList";
import SmartlistPage from "../smartlist/SmartlistPage";
import SmartlistAudioList from "../smartlist_audio/SmartlistAudioList";
import SmartlistCollectionsList from "../smartlist_collections/SmartlistCollectionsList";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

function ProjectUpdate(): JSX.Element {
  const dispatch = useDispatch();
  const { getHomePageURL } = useAbility();

  const homePageUrl = getHomePageURL();
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const currentProject = projects?.data.find(
    (project) => project.name === projectSlug,
  );

  useEffect(() => {
    if (currentProject) {
      dispatch(setProjectUuid(currentProject.uuid));
      dispatch(setProject({ project: currentProject, projectSlug }));
    }
    return () => {
      dispatch(setProjectUuid(null));
      dispatch(setProject({ project: null, projectSlug: null }));
    };
  }, [projectSlug, currentProject, dispatch]);

  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const { canAccess } = useAbility();

  const { project: canAccessProject } = canAccess(orgaOptions);

  const isEduProject = currentProject?.type === TypeEnum.Edu;

  if (projects.isFetched && !currentProject) {
    return <Redirect to={homePageUrl} />;
  }

  return (
    <React.Fragment>
      <Route
        exact
        component={DashboardAdmin}
        path={`${PROJECTS_ROUTE}/:projectSlug/dashboard`}
      />
      {canAccessProject.projectWrite ? (
        <Route
          key="project_update"
          path={`${PROJECTS_ROUTE}/:projectSlug/edit`}
        >
          <ProjectAdminUpdate />
        </Route>
      ) : null}
      {isEduProject ? (
        <React.Fragment>
          <Route
            exact
            component={EDUAdminInstitutions}
            path={`${PROJECTS_ROUTE}/:projectSlug/institutions`}
          />
          <Route
            key="edu_create"
            component={EDUAdminCreate}
            path={`${PROJECTS_ROUTE}/:projectSlug/institutions/new`}
          />
          <Route
            key="edu_update"
            exact
            path={`${PROJECTS_ROUTE}/:projectSlug/institutions/edit/:id`}
          >
            <EDUAdminUpdate />
          </Route>
        </React.Fragment>
      ) : null}
      {!isEduProject && (
        <React.Fragment>
          {canAccessProject.allUsers ? (
            <Route
              key="users_index"
              exact
              component={UsersAdmin}
              path={`${PROJECTS_ROUTE}/:projectSlug/users`}
            />
          ) : null}
          {canAccessProject.freeCopies ? (
            <React.Fragment>
              <Route
                key="freecopy_index"
                exact
                component={FreecopyList}
                path={`${PROJECTS_ROUTE}/:projectSlug/freecopy`}
              />
              {canAccessProject.freeCopiesWrite ? (
                <React.Fragment>
                  <Route
                    key="freecopy_update"
                    component={FreecopyPage}
                    path={`${PROJECTS_ROUTE}/:projectSlug/freecopy/:action/:id?`}
                  />
                  <Route
                    key="freecopy_unique_code_update"
                    component={FreecopyUniqueCodePage}
                    path={`${PROJECTS_ROUTE}/:projectSlug/freecopy-code/:action/:id?`}
                  />
                </React.Fragment>
              ) : null}
            </React.Fragment>
          ) : null}
          {canAccessProject.gifts ? (
            <React.Fragment>
              <Route
                key="gift_index"
                exact
                component={GiftList}
                path={`${PROJECTS_ROUTE}/:projectSlug/gift`}
              />
              <Route
                key="gift_update"
                component={GiftAdminUpdate}
                path={`${PROJECTS_ROUTE}/:projectSlug/gift/edit/:id`}
              />
              {canAccessProject.giftsWrite ? (
                <Route
                  key="gift_create"
                  component={GiftAdminCreate}
                  path={`${PROJECTS_ROUTE}/:projectSlug/gift/new/`}
                />
              ) : null}
            </React.Fragment>
          ) : null}
          {canAccessProject.usersAnalytics ? (
            <React.Fragment>
              <Route
                key="users_index"
                exact
                component={UsersAdmin}
                path={`${PROJECTS_ROUTE}/:projectSlug/analytics/users`}
              />
              <Route
                key="user_update"
                component={UsersAdminUpdate}
                path={`${PROJECTS_ROUTE}/:projectSlug/users/edit/:id`}
              />
              {canAccessProject.usersWrite ? (
                <Route
                  key="user_create"
                  component={UsersAdminCreate}
                  path={`${PROJECTS_ROUTE}/:projectSlug/users/new/`}
                />
              ) : null}
            </React.Fragment>
          ) : null}
        </React.Fragment>
      )}
      {canAccessProject.layout ? <PlatformRoutes /> : null}
      {canAccessProject.configuration ? (
        <Route
          exact
          component={ConfigurationAdmin}
          path={`${PROJECTS_ROUTE}/:projectSlug/configuration`}
        />
      ) : null}
      {canAccessProject.layout ? (
        <Route
          exact
          component={AppearanceIndex}
          path={`${PROJECTS_ROUTE}/:projectSlug/appearance`}
        />
      ) : null}
      {canAccessProject.offers ? (
        <Route
          exact
          component={OfferList}
          path={`${PROJECTS_ROUTE}/:projectSlug/offers`}
        />
      ) : null}
      {canAccessProject.advertisingsSponsorings ? (
        <React.Fragment>
          <Route
            exact
            component={AdvertisingsList}
            path={`${PROJECTS_ROUTE}/:projectSlug/advertisings`}
          />
          <Route
            key="campaigns_edit"
            component={AdvertisingsPage}
            path={`${PROJECTS_ROUTE}/:projectSlug/advertisings/edit/:id?`}
          />
          {canAccessProject.advertisingsSponsoringsWrite ? (
            <Route
              key="campaigns_new"
              component={AdvertisingsCreate}
              path={`${PROJECTS_ROUTE}/:projectSlug/advertisings/new/:type`}
            />
          ) : null}
        </React.Fragment>
      ) : null}
      {canAccessProject.displays ? (
        <React.Fragment>
          <Route
            exact
            component={DisplaysList}
            path={`${PROJECTS_ROUTE}/:projectSlug/displays`}
          />
          <Route
            key="displays_edit"
            component={DisplaysPage}
            path={`${PROJECTS_ROUTE}/:projectSlug/displays/:id`}
          />
        </React.Fragment>
      ) : null}
      {canAccessProject.notifications ? (
        <Route
          exact
          component={NotificationsIndex}
          path={`${PROJECTS_ROUTE}/:projectSlug/notifications`}
        />
      ) : null}
      {canAccessProject.smartlists ? (
        <React.Fragment>
          <Route
            exact
            component={SmartlistList}
            path={`${PROJECTS_ROUTE}/:projectSlug/smartlist`}
          />
          <Route
            path={[
              `${PROJECTS_ROUTE}/:projectSlug/smartlist/:action/:id?`,
              `${PROJECTS_ROUTE}/:projectSlug/smartlist-standard/:action/:id?`,
            ]}
          >
            <SmartlistPage smartlistCreationType={SmartlistType.STANDARD} />
          </Route>
          <Route
            path={`${PROJECTS_ROUTE}/:projectSlug/smartlist-auto/:action/:id?`}
          >
            <SmartlistPage smartlistCreationType={SmartlistType.AUTO} />
          </Route>
        </React.Fragment>
      ) : null}
      {canAccessProject.collections ? (
        <React.Fragment>
          <Route
            exact
            component={SmartlistCollectionsList}
            path={`${PROJECTS_ROUTE}/:projectSlug/collections`}
          />
          <Route
            path={`${PROJECTS_ROUTE}/:projectSlug/collections/:action/:id?`}
          >
            <SmartlistPage smartlistCreationType={SmartlistType.COLLECTION} />
          </Route>
        </React.Fragment>
      ) : null}
      {canAccessProject.series ? (
        <React.Fragment>
          <Route
            key="path-to-series"
            exact
            component={SmartlistSeriesList}
            path={`${PROJECTS_ROUTE}/:projectSlug/series`}
          />
          <Route path={[`${PROJECTS_ROUTE}/:projectSlug/series/:action/:id?`]}>
            <SeriePage />
          </Route>
        </React.Fragment>
      ) : null}
      {canAccessProject.smartlistsAudios ? (
        <React.Fragment>
          <Route
            exact
            component={SmartlistAudioList}
            path={`${PROJECTS_ROUTE}/:projectSlug/smartlist_audio`}
          />
          <Route
            component={SmartlistPage}
            path={`${PROJECTS_ROUTE}/:projectSlug/smartlist_audio/:action/:id?`}
          >
            <SmartlistPage smartlistCreationType={SmartlistType.AUDIO} />
          </Route>
        </React.Fragment>
      ) : null}
      {canAccessProject.contentsAnalytics ? (
        <React.Fragment>
          <Route
            key="path-to-analytics/content"
            exact
            component={ContentAnalyticsAdmin}
            path={`${PROJECTS_ROUTE}/:projectSlug/analytics/content`}
          />
          <Route
            key="path-to-analytics/content/:id"
            exact
            component={ContentItemAnalytics}
            path={`${PROJECTS_ROUTE}/:projectSlug/analytics/content/:id`}
          />
        </React.Fragment>
      ) : null}
      {canAccessProject.transactionsAnalytics ? (
        <Route
          key="path-to-analytics/transactions"
          exact
          component={TransactionsAnalyticsAdmin}
          path={`${PROJECTS_ROUTE}/:projectSlug/analytics/transactions`}
        />
      ) : null}
      {canAccessProject.offersAnalytics ? (
        <Route
          key="path-to-analytics/offer"
          exact
          component={OfferAnalytics}
          path={`${PROJECTS_ROUTE}/:projectSlug/analytics/offer`}
        />
      ) : null}
      {canAccessProject.offersView ? (
        <Route
          key="offers_view"
          component={OfferView}
          path={`${PROJECTS_ROUTE}/:projectSlug/offer/view/:id`}
        />
      ) : null}
      {canAccessProject.offersWrite ? (
        <Route
          key="offers_edit"
          component={OffersEdit}
          path={`${PROJECTS_ROUTE}/:projectSlug/offers/:action/:id?`}
        />
      ) : null}
      {canAccessProject.community ? (
        <React.Fragment>
          <Route
            key="comments_list"
            exact
            component={Comments}
            path={`${PROJECTS_ROUTE}/:projectSlug/comments`}
          />
          <Route
            key="comments_view"
            component={Comment}
            path={`${PROJECTS_ROUTE}/:projectSlug/comments/view/:id`}
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
}

function ProjectCreateOrUpdate(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();

  if (projectSlug === "new") return <ProjectAdminCreate />;

  return <ProjectUpdate />;
}

function ProjectRoutes(): JSX.Element {
  return (
    <React.Fragment>
      <Route exact component={ProjectList} path={PROJECTS_ROUTE} />
      <Route
        component={ProjectCreateOrUpdate}
        path={`${PROJECTS_ROUTE}/:projectSlug`}
      />
    </React.Fragment>
  );
}

export default ProjectRoutes;
